import { wait } from "../wait";
import { blockifyFile } from "./blockifyFile";
import { commitBlocks } from "./commitBlocks";
import type { AzureBlock, AzureBlockUploadStatus } from "./shared";
import { uploadBlock } from "./uploadBlock";

const UPLOAD_CHUNK_BYTES = 1024 * 1024 * 1; // 1MB
const MAX_BLOCK_UPLOAD_ATTEMPTS = 3;
const QUEUE_CONCURRENCY = 3;

export type BlockInfo = {
  id: number;
  size: number;
  status: AzureBlockUploadStatus;
};

export type AzureUploadEvent =
  | {
      type: "ChunkFile";
      blockInfos: BlockInfo[];
      totalSize: number;
      fileType: string;
      fileName: string;
    }
  | { type: "BlocksUploadStart" }
  | {
      type: "BlockStatusChange";
      blockInfo: BlockInfo;
    }
  | { type: "BlocksUploadComplete" }
  | { type: "CommitBlocksStart" }
  | { type: "CommitBlocksComplete" };

export type UploadErrorType =
  | "EmptyAccessToken"
  | "EmptyFile"
  | "UploadFailed"
  | "CommitFailed";

type QueueItem = { attempts: number; block: AzureBlock };

export type UploadErrorResult = {
  ok: false;
  errorType: UploadErrorType;
  message?: string;
};

export type AbortUploadResult = {
  ok: false;
  errorType: "Aborted";
  message?: string;
};
export type OkResult = { ok: true };
export type AzureUploadEventHandler = (event: AzureUploadEvent) => void;

export function getAzureUploadProgressEventHandler(
  progressCb?:
    | ((progress: number | null) => void)
    | ((progress: number) => void),
  progressRangeStart: number = 0,
  progressRangeEnd: number = 100
): AzureUploadEventHandler {
  let totalSize = -1;
  let uploadedSize = 0;
  if (!progressCb) {
    return () => {};
  }
  return (e: AzureUploadEvent) => {
    if (e.type === "ChunkFile") {
      totalSize = e.totalSize;
      return;
    }
    if (e.type === "BlockStatusChange" && e.blockInfo.status === "UPLOADED") {
      if (totalSize <= 0) {
        return;
      }
      uploadedSize += e.blockInfo.size;
      const progress =
        Math.round(
          (uploadedSize / totalSize) * (progressRangeEnd - progressRangeStart)
        ) + progressRangeStart;
      progressCb(progress);
    }
  };
}

export async function uploadToAzureBlobStorage(
  file: File,
  accessToken: string,
  handleEvents?: AzureUploadEventHandler | undefined,
  abortSignal?: AbortSignal
): Promise<OkResult | UploadErrorResult | AbortUploadResult> {
  if (!accessToken) {
    return { ok: false, errorType: "EmptyAccessToken" };
  }
  if (file.size === 0) {
    return { ok: false, errorType: "EmptyFile" };
  }
  const blocks = blockifyFile(file, UPLOAD_CHUNK_BYTES);
  handleEvents?.({
    type: "ChunkFile",
    blockInfos: blocks.map((block) => ({
      id: block.id,
      size: block.data.size,
      status: "PENDING",
    })),
    totalSize: file.size,
    fileType: file.type,
    fileName: file.name,
  });
  handleEvents?.({ type: "BlocksUploadStart" });
  const uploadQueue: QueueItem[] = blocks.map((block) => ({
    attempts: 0,
    block,
  }));
  let stopWork = false;
  async function doWork() {
    while (uploadQueue.length > 0 && !stopWork && !abortSignal?.aborted) {
      const queueItem = uploadQueue.shift()!;
      const result = await uploadBlockWithRetries(
        queueItem,
        accessToken,
        handleEvents,
        abortSignal
      );
      if (!result.ok) {
        if (result.type === "AttemptsExceeded") {
          stopWork = true;
          return {
            ok: false,
            errorType: "UploadFailed",
            message: `Block ${queueItem.block.id} failed to upload after ${MAX_BLOCK_UPLOAD_ATTEMPTS} attempts`,
          };
        }
        queueItem.attempts += 1;
        uploadQueue.push(queueItem);
      }
    }
    return { ok: true };
  }
  const runningWorkers = [];
  for (let i = 0; i < QUEUE_CONCURRENCY; i++) {
    runningWorkers.push(doWork());
  }
  const workerResults = await Promise.all(runningWorkers);
  const failedRes = workerResults.find((res) => !res.ok);
  if (failedRes && !failedRes.ok) {
    return { ok: false, errorType: "UploadFailed", message: failedRes.message };
  }
  handleEvents?.({ type: "BlocksUploadComplete" });
  handleEvents?.({ type: "CommitBlocksStart" });
  const commitResult = await commitBlocks(
    blocks.map((block) => block.id),
    accessToken,
    file.type,
    abortSignal
  );
  if (!commitResult.ok) {
    return {
      ok: false,
      errorType: "CommitFailed",
      message: commitResult.message,
    };
  }
  handleEvents?.({ type: "CommitBlocksComplete" });
  return { ok: true };
}

async function uploadBlockWithRetries(
  queueItem: QueueItem,
  accessToken: string,
  handleEvents?: (event: AzureUploadEvent) => void,
  abortSignal?: AbortSignal
): Promise<
  | { ok: true }
  | { ok: false; type: "AttemptsExceeded" }
  | { ok: false; type: "UploadFailed" }
> {
  const { block, attempts } = queueItem;
  if (attempts > 0) {
    await wait(attempts * 50);
  }
  if (attempts >= MAX_BLOCK_UPLOAD_ATTEMPTS) {
    return {
      ok: false,
      type: "AttemptsExceeded",
    };
  }
  handleEvents?.({
    type: "BlockStatusChange",
    blockInfo: { id: block.id, size: block.data.size, status: "UPLOADING" },
  });
  const uploadResult = await uploadBlock(block, accessToken, abortSignal);
  if (!uploadResult.ok) {
    handleEvents?.({
      type: "BlockStatusChange",
      blockInfo: { id: block.id, size: block.data.size, status: "ERROR" },
    });
    return { ok: false, type: "UploadFailed" };
  }
  handleEvents?.({
    type: "BlockStatusChange",
    blockInfo: { id: block.id, size: block.data.size, status: "UPLOADED" },
  });
  return { ok: true };
}
